import { useNavigate } from 'react-router-dom'
import styled from '@emotion/styled'
import { OrderType, RevenueCenter, ServiceType } from '@open-tender/types'
import { makeRevenueCenterMsg } from '@open-tender/utils'
import {
  openModal,
  selectAutoSelect,
  selectContent,
  selectGeoLatLng,
  selectGroupOrder,
  selectIsGroupOrder,
  selectOrder,
  setAddress,
  setOrderServiceType,
  setRequestedAt,
  setRevenueCenter,
  useAppDispatch,
  useAppSelector,
} from '@open-tender/cloud'
import { Button } from 'components'
import Text from 'components/Text'
import { sendLocationSelected, sendStoreSelected } from 'app/analytics'
import { useNearestFirstLocations } from 'hooks'

const RevenueCenterOrderView = styled.div`
  margin: 1.75rem 0 0;

  @media (max-width: ${(props) => props.theme.breakpoints.mobile}) {
    margin: 1.25rem 0 0;
  }
`

const RevenueCenterOrderMessage = styled(Text)`
  font-size: ${(props) => props.theme.fonts.sizes.small};
  line-height: ${(props) => props.theme.fonts.body.lineHeight};
  @media (max-width: ${(props) => props.theme.breakpoints.mobile}) {
    font-size: ${(props) => props.theme.fonts.sizes.xSmall};
  }
`

const RevenueCenterOrderButtons = styled.div`
  margin-top: 1.75rem;

  @media (max-width: ${(props) => props.theme.breakpoints.mobile}) {
    margin-top: 1.25rem;
  }

  button {
    margin-right: 1rem;

    &:last-of-type {
      margin-right: 0;
    }
  }
`

const RevenueCenterChange = ({ autoSelect }: { autoSelect?: boolean }) => {
  const navigate = useNavigate()
  if (autoSelect) return null

  return (
    <RevenueCenterOrderButtons>
      <Button onClick={() => navigate(`/locations`)} size="small">
        Change Location
      </Button>
    </RevenueCenterOrderButtons>
  )
}

const RevenueCenterChoose = ({
  revenueCenter,
  serviceType,
  orderType,
  statusMessage,
}: {
  revenueCenter: RevenueCenter
  serviceType: ServiceType
  orderType: OrderType
  statusMessage: string | null
}) => {
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const {
    slug,
    revenue_center_type: rcType,
    is_outpost: isOutpost,
    address,
    first_times,
    order_times,
    status,
  } = revenueCenter
  const menuSlug = `/menu/${slug}`
  const firstTimes = first_times ? first_times[serviceType] : null
  const orderTimes = order_times ? order_times[serviceType] : null
  const { cartId } = useAppSelector(selectGroupOrder)
  const isGroupOrder = useAppSelector(selectIsGroupOrder)
  const nearestFirst = useNearestFirstLocations()
  const geoLatLng = useAppSelector(selectGeoLatLng)
  const { address: enteredAddress } = useAppSelector(selectOrder)
  const needsConfirm = nearestFirst && !geoLatLng && !!enteredAddress

  if (!firstTimes && !orderTimes) return null

  if (status !== 'OPEN') return null

  const isCatering = rcType === 'CATERING'

  const startAsap = () => {
    dispatch(setRequestedAt('asap'))
    dispatch(setRevenueCenter(revenueCenter))
    dispatch(setOrderServiceType({ orderType: rcType, serviceType, isOutpost }))
    if (isOutpost) dispatch(setAddress(address))
    sendLocationSelected({
      name: revenueCenter.name,
      revenue_center_id: revenueCenter.revenue_center_id,
    })
    navigate(menuSlug)
  }

  const startLater = () => {
    const args = {
      revenueCenter,
      serviceType,
      orderType,
      isGroupOrder: !!(isGroupOrder || cartId),
      focusFirst: true,
      skipClose: true,
      style: orderTimes ? { alignItems: 'flex-start' } : {},
    }
    dispatch(openModal({ type: 'requestedAt', args }))
    sendLocationSelected({
      name: revenueCenter.name,
      revenue_center_id: revenueCenter.revenue_center_id,
    })
  }

  const confirmStore = (onConfirm: () => void) => {
    dispatch(
      openModal({
        type: 'confirmStore',
        args: { revenueCenter, onConfirm, preventClose: true },
      })
    )
  }

  const orderAsap = () => {
    if (statusMessage !== 'Open Now') return
    sendStoreSelected({
      name: revenueCenter.name,
      revenue_center_id: revenueCenter.revenue_center_id,
    })
    if (needsConfirm) return confirmStore(startAsap)
    startAsap()
  }

  const orderLater = () => {
    sendStoreSelected({
      name: revenueCenter.name,
      revenue_center_id: revenueCenter.revenue_center_id,
    })
    if (needsConfirm) return confirmStore(startLater)
    startLater()
  }

  return (
    <RevenueCenterOrderButtons>
      {!isCatering && firstTimes && (
        <Button
          onClick={orderAsap}
          disabled={statusMessage !== 'Open Now'}
          size="small"
        >
          Order Now
        </Button>
      )}
      <Button
        onClick={orderLater}
        size="small"
        color={isCatering || !firstTimes ? 'primary' : 'secondary'}
      >
        {isCatering ? 'Order from Here' : 'Order for Later'}
      </Button>
    </RevenueCenterOrderButtons>
  )
}

export const RevenueCenterOrder = ({
  revenueCenter,
  isMenu,
  isLanding,
  statusMessage,
}: {
  revenueCenter: RevenueCenter
  isMenu?: boolean
  isOrder?: boolean
  isLanding?: boolean
  statusMessage: string | null
}) => {
  const { orderType, serviceType, requestedAt } = useAppSelector(selectOrder)
  const { cartId } = useAppSelector(selectGroupOrder)
  const hasGroupOrdering = revenueCenter && revenueCenter.group_ordering
  const autoSelect = useAppSelector(selectAutoSelect)
  const { revenueCenters: rcConfig } = useAppSelector(selectContent) || {}
  const { statusMessages } = rcConfig || {}
  const msg =
    statusMessages &&
    makeRevenueCenterMsg(
      revenueCenter,
      serviceType ?? 'WALKIN',
      requestedAt ?? 'asap',
      statusMessages as any,
      true
    )
  const showMsg = !!(msg?.message && !isMenu)

  return (
    <RevenueCenterOrderView>
      {cartId && !hasGroupOrdering ? (
        <RevenueCenterOrderMessage>
          This location does not offer group ordering.
        </RevenueCenterOrderMessage>
      ) : (
        <>
          {showMsg && (
            <RevenueCenterOrderMessage as="p" color="alert">
              {msg?.message}
            </RevenueCenterOrderMessage>
          )}
          {isMenu ? (
            <RevenueCenterChange autoSelect={autoSelect} />
          ) : isLanding ? null : serviceType && orderType ? (
            <RevenueCenterChoose
              statusMessage={statusMessage}
              revenueCenter={revenueCenter}
              serviceType={serviceType}
              orderType={orderType}
            />
          ) : null}
        </>
      )}
    </RevenueCenterOrderView>
  )
}

export default RevenueCenterOrder
